(() => {
  const SELECTORS = {
    finder: '[data-pp7-feel]',
    trigger: '[data-pp7-feel-tab]',
    panel: '[data-pp7-feel-panel]',
    dialog: '[data-pp7-feel-dialog]',
    results: '[data-pp7-feel-results]',
    close: '[data-pp7-feel-close]',
    previous: '[data-pp7-feel-prev]',
    next: '[data-pp7-feel-next]',
    counter: '[data-pp7-feel-counter]',
    dialogLabel: '[data-pp7-feel-dialog-label]',
    galleryStatus: '[data-pp7-feel-gallery-status]',
    status: '[data-pp7-feel-status]'
  };

  const initializeFinder = (finder) => {
    if (finder.dataset.pp7Enhanced === 'true') return;

    const triggers = Array.from(finder.querySelectorAll(SELECTORS.trigger));
    const panels = Array.from(finder.querySelectorAll(SELECTORS.panel));
    const dialog = finder.querySelector(SELECTORS.dialog);
    const results = finder.querySelector(SELECTORS.results);
    const closeButton = finder.querySelector(SELECTORS.close);
    const previousButton = finder.querySelector(SELECTORS.previous);
    const nextButton = finder.querySelector(SELECTORS.next);
    const counter = finder.querySelector(SELECTORS.counter);
    const dialogLabel = finder.querySelector(SELECTORS.dialogLabel);
    const galleryStatus = finder.querySelector(SELECTORS.galleryStatus);
    const status = finder.querySelector(SELECTORS.status);
    const stage = dialog?.querySelector('.pp7-feel-lightbox__stage');

    if (
      !dialog ||
      !results ||
      !closeButton ||
      !previousButton ||
      !nextButton ||
      !counter ||
      !dialogLabel ||
      !stage ||
      triggers.length === 0 ||
      triggers.length !== panels.length
    ) return;

    const entries = triggers.map((trigger) => {
      const targetId = trigger.dataset.pp7FeelTarget;
      const panel = panels.find((candidate) => candidate.id === targetId);
      return panel ? { trigger, panel } : null;
    });

    if (entries.some((entry) => !entry)) return;

    finder.dataset.pp7Enhanced = 'true';
    const controller = new AbortController();
    const eventOptions = { signal: controller.signal };
    let activeEntryIndex = -1;
    let activeSlideIndex = 0;
    let activeSlides = [];
    let lastTrigger = null;
    let restoreFocusOnClose = true;
    let touchStart = null;
    const ageVerifier = document.getElementById('AgeVerifier');
    const hasAgeCookie = () => document.cookie.split(';').some((cookie) => cookie.trim().startsWith('concept:age-verifier='));
    let ageGateResolved = !ageVerifier || hasAgeCookie();

    entries.forEach(({ panel }) => {
      panel.hidden = true;
      results.appendChild(panel);
    });

    const setScrollLock = (locked) => {
      document.documentElement.classList.toggle('pp7-feel-lightbox-open', locked);
      document.body.classList.toggle('pp7-feel-lightbox-open', locked);
    };

    const updateSlide = (index, options = {}) => {
      const slideCount = activeSlides.length;
      activeSlideIndex = slideCount > 0 ? Math.max(0, Math.min(index, slideCount - 1)) : 0;

      activeSlides.forEach((slide, slideIndex) => {
        slide.hidden = slideIndex !== activeSlideIndex;
      });
      stage.scrollTop = 0;

      const hasMultipleSlides = slideCount > 1;
      previousButton.disabled = !hasMultipleSlides;
      nextButton.disabled = !hasMultipleSlides;
      previousButton.setAttribute('aria-disabled', !hasMultipleSlides || activeSlideIndex === 0 ? 'true' : 'false');
      nextButton.setAttribute('aria-disabled', !hasMultipleSlides || activeSlideIndex === slideCount - 1 ? 'true' : 'false');

      if (slideCount === 0) {
        counter.textContent = 'NO PREVIEW';
        if (galleryStatus && options.announce) galleryStatus.textContent = 'No in-stock preview is available. Use the shop all matches link to browse the full selection.';
        return;
      }

      counter.textContent = `${String(activeSlideIndex + 1).padStart(2, '0')} / ${String(slideCount).padStart(2, '0')}`;
      const productTitle = activeSlides[activeSlideIndex].querySelector('h4')?.textContent.trim() || 'product';

      if (galleryStatus && options.announce) {
        galleryStatus.textContent = `Product ${activeSlideIndex + 1} of ${slideCount}: ${productTitle}.`;
      }
    };

    const moveSlide = (index, options = {}) => {
      if (index < 0 || index >= activeSlides.length || index === activeSlideIndex) return;
      updateSlide(index, options);
    };

    const selectFeeling = (index) => {
      activeEntryIndex = Math.max(0, Math.min(index, entries.length - 1));
      const activeEntry = entries[activeEntryIndex];
      const label = activeEntry.trigger.dataset.pp7FeelLabel || 'Feeling';

      entries.forEach(({ trigger, panel }, entryIndex) => {
        const selected = entryIndex === activeEntryIndex;
        trigger.classList.toggle('is-active', selected);
        panel.hidden = !selected;
      });

      const tone = window.getComputedStyle(activeEntry.trigger).getPropertyValue('--pp7-tone').trim();
      if (tone) dialog.style.setProperty('--pp7-lightbox-tone', tone);

      dialogLabel.textContent = label;
      previousButton.setAttribute('aria-label', `Previous ${label} product`);
      nextButton.setAttribute('aria-label', `Next ${label} product`);
      activeSlides = Array.from(activeEntry.panel.querySelectorAll('.pp7-feel-product'));
      updateSlide(0, { announce: false });
    };

    const openGallery = (index) => {
      const activeEntry = entries[index];
      if (!activeEntry) return;

      const ageGatePending = ageVerifier && !ageGateResolved && (ageVerifier.dataset.testMode === 'true' || !hasAgeCookie());
      if (ageGatePending) {
        if (!ageVerifier.hasAttribute('open') && typeof ageVerifier.show === 'function') ageVerifier.show();
        return;
      }

      if (ageVerifier?.hasAttribute('open')) return;

      if (typeof dialog.showModal !== 'function') {
        window.location.assign(activeEntry.trigger.dataset.pp7FeelUrl || '/collections/all');
        return;
      }

      lastTrigger = activeEntry.trigger;
      restoreFocusOnClose = true;
      if (galleryStatus) galleryStatus.textContent = '';
      selectFeeling(index);

      if (!dialog.open) dialog.showModal();
      setScrollLock(true);

      window.requestAnimationFrame(() => {
        if (!dialog.open) return;
        closeButton.focus({ preventScroll: true });
        updateSlide(0, { announce: true });
      });
    };

    const closeGallery = (shouldRestoreFocus = true) => {
      if (!dialog.open) return;
      restoreFocusOnClose = shouldRestoreFocus;
      dialog.close();
    };

    entries.forEach(({ trigger }, index) => {
      trigger.addEventListener('click', () => openGallery(index), eventOptions);
    });

    closeButton.addEventListener('click', () => closeGallery(true), eventOptions);
    previousButton.addEventListener('click', () => moveSlide(activeSlideIndex - 1, { announce: true }), eventOptions);
    nextButton.addEventListener('click', () => moveSlide(activeSlideIndex + 1, { announce: true }), eventOptions);

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeGallery(true);
    }, eventOptions);

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeGallery(true);
        return;
      }

      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.target.closest('.pp7-feel-product, input, select, textarea, [contenteditable="true"]')) return;

      let nextIndex;
      if (event.key === 'ArrowLeft') {
        nextIndex = activeSlideIndex - 1;
      } else if (event.key === 'ArrowRight') {
        nextIndex = activeSlideIndex + 1;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = activeSlides.length - 1;
      } else {
        return;
      }

      if (nextIndex < 0 || nextIndex >= activeSlides.length) return;
      event.preventDefault();
      moveSlide(nextIndex, { announce: true });
    }, eventOptions);

    stage.addEventListener('touchstart', (event) => {
      const touch = event.changedTouches[0];
      touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
    }, { passive: true, signal: controller.signal });

    stage.addEventListener('touchend', (event) => {
      const touch = event.changedTouches[0];
      if (!touchStart || !touch) return;

      const deltaX = touch.clientX - touchStart.x;
      const deltaY = touch.clientY - touchStart.y;
      touchStart = null;

      if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      const nextIndex = deltaX < 0 ? activeSlideIndex + 1 : activeSlideIndex - 1;
      moveSlide(nextIndex, { announce: true });
    }, { passive: true, signal: controller.signal });

    dialog.addEventListener('close', () => {
      setScrollLock(false);
      entries.forEach(({ trigger }) => trigger.classList.remove('is-active'));
      if (status && restoreFocusOnClose && activeEntryIndex >= 0) status.textContent = `${entries[activeEntryIndex].trigger.dataset.pp7FeelLabel || 'Feeling'} gallery closed.`;

      if (restoreFocusOnClose && lastTrigger && document.contains(lastTrigger)) {
        lastTrigger.focus({ preventScroll: true });
      }
    }, eventOptions);

    document.addEventListener('toggle', (event) => {
      if (event.target?.id === 'AgeVerifier' && event.target.hasAttribute('open')) closeGallery(false);
    }, eventOptions);

    document.addEventListener('click', (event) => {
      if (event.target.closest('#AgeVerifier .cookie__buttons button[aria-controls="AgeVerifier"]')) ageGateResolved = true;
    }, eventOptions);

    finder.pp7Cleanup = () => {
      if (dialog.open) closeGallery(false);
      setScrollLock(false);
      controller.abort();
    };
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll(SELECTORS.finder).forEach(initializeFinder);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(SELECTORS.finder).forEach((finder) => finder.pp7Cleanup?.());
  });
})();
